import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, FolderOpen, LifeBuoy } from "lucide-react";

/**
 * WizardErrorBoundary
 * Catches render/runtime errors inside the Grievance Builder so a single glitch
 * never wipes out a member's draft mid-flow. Autosave already persists edits to
 * the backend every ~900ms, so the last saved draft is safe on our servers.
 */
export class WizardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[WizardErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    // Full page reload — fetches the latest autosaved draft from the backend.
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // Try to recover the case id from the URL so we can offer a direct reopen link.
    const match = typeof window !== "undefined"
      ? window.location.pathname.match(/\/new-grievance\/([^/?#]+)/)
      : null;
    const caseId = match ? match[1] : null;

    return (
      <div className="min-h-[70vh] bg-slate-50 py-12 px-4" data-testid="wizard-error-boundary">
        <div className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-lg shadow-sm p-8">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-black font-display text-slate-900">
                Something went wrong — but your draft is safe.
              </h1>
              <p className="mt-3 text-slate-700 leading-relaxed">
                The grievance builder hit an unexpected error. Your work autosaves to your
                account every few seconds, so your latest saved draft is preserved.
                Reload the wizard to pick up exactly where you left off.
              </p>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="wizard-error-reload-btn"
                  onClick={this.handleReload}
                  className="bg-red-600 hover:bg-red-700 text-white h-11 px-5"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reload My Draft
                </Button>
                <Link to="/dashboard">
                  <Button
                    data-testid="wizard-error-dashboard-btn"
                    variant="outline"
                    className="h-11 px-5 border-slate-300 w-full sm:w-auto"
                  >
                    <FolderOpen className="h-4 w-4 mr-2" />
                    Return to Dashboard
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    data-testid="wizard-error-contact-btn"
                    variant="ghost"
                    className="h-11 px-5 text-slate-700 w-full sm:w-auto"
                  >
                    <LifeBuoy className="h-4 w-4 mr-2" />
                    Report This
                  </Button>
                </Link>
              </div>

              {caseId && (
                <p className="mt-6 text-xs text-slate-500" data-testid="wizard-error-case-id">
                  Draft reference: <span className="font-mono">{caseId}</span>
                </p>
              )}

              {this.state.error?.message && (
                <details className="mt-6 text-xs text-slate-500">
                  <summary className="cursor-pointer select-none">Technical details</summary>
                  <pre className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded overflow-auto text-slate-600">
{String(this.state.error.message)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default WizardErrorBoundary;
